import React, {useEffect, useReducer, useRef, useState} from 'react'// eslint-disable-line
import {createUseStyles} from 'react-jss';
import {banksReducer} from '../reducers/banks-reducer';
import apiEntryPoint from '../api/apiSingleton';
import {
  dateFormatter,
  dateFormatterText,
  getCityTranslateByKey,
  getKzDateTime, getSavedLocation,
  getUrlParameters,
  platform
} from '../utils/helpers';
import DynamicComponent from '../components/common/dynamicComponent';
import {CustomDatePicker} from '../components/form-elements/date-pickers/date-picker';
import {BanksTable} from '../components/tables/banks-table';
import {COLORS} from '../utils/style-variables';
import {
  CHANGE_CURRENCY,
  FETCH_BANKS_ERROR,
  FETCH_BANKS_PENDING,
  FETCH_BANKS_SUCCESS, LOCATION_TRANSLATE, SELECT_DATE, SELECT_LOCATION
} from '../utils/action-types';
import {groupedCurrency} from '../utils/currency-names';
import {HeaderMetaTags} from '../components/common/meta-tags';
import {banksH1Text, banksTitleText} from '../utils/texts';
import {DEFAULT_CURRENCY, DEFAULT_LANGUAGE} from '../utils/constants';
import {WidgetH1} from '../components/common/widget-h1';
import {getBanksJSON_LD} from '../utils/ld_json/banks';
import {Loader} from '../components/common/loader';

const useStyles = createUseStyles({
  widgetBlock: {
    position: 'relative',
    padding: '26px 32px',
    marginBottom: 15,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: COLORS.lightGrey30Color,
    borderRadius: 10
  },
  widgetBlockMobile: {
    padding: 0,
    border: 'none'
  },
  currencyWrapper: {
    marginBottom: 18,
    minHeight: 37
  },
  widget: {
    extend: 'widgetBlock',
    minHeight: 308,
    marginBottom: 20
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 18,
    minHeight: 38
  },
  selectWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '@media (max-width: 768px)': {
      marginTop: 15
    }
  },
  dateWrapper: {
    display: 'inline-block'
  },
  '@media (max-width: 768px)': {
    currencyWrapper: {
      marginBottom: 15,
      minHeight: 40
    },
    widget: {
      extend: 'widgetBlockMobile',
      minHeight: 271
    },
    header: {
      display: 'block',
      marginBottom: 12
    },
    titleText: {
      letterSpacing: '0.2px',
      marginBottom: 15
    },
    dateWrapper: {
      width: 'calc(50% - 8px)'
    }
  }
});

const loadDesktopCurrencySelector = () =>
  import(/* webpackChunkName: "selector" */'../components/form-elements/desktop-currency-select');
const loadMobileCurrencySelector = () =>
  import(/* webpackChunkName: "selector" */'../components/form-elements/mobile-currency-select');
const loadDesktopLocationSelector = () =>
  import(/* webpackChunkName: "selector" */'../components/form-elements/desktop-location-select');
const loadMobileLocationSelector = () =>
  import(/* webpackChunkName: "selector" */'../components/form-elements/mobile-location-select');

const savedLocation = getSavedLocation();
const getSavedDate = getUrlParameters('date') ? new Date(getUrlParameters('date')) : new Date();
const getLocationTranslate = getCityTranslateByKey(savedLocation.key);

const initialState = {
  data: [],
  lastModified: '',
  sorted: null,
  isFetching: false,
  isError: false,
  open: false,
  open_location_select: false,
  radio: DEFAULT_CURRENCY.value,
  value: DEFAULT_CURRENCY,
  date: getSavedDate,
  selectedLocation: savedLocation,
  locationTranslate: getLocationTranslate,
  savedBranches: []
};

export const BanksCurrency = () => {
  const [state, dispatch] = useReducer(banksReducer, initialState);
  const [flag, setFlag] = useState(true);
  const [timeoutError, setTimeoutError] = useState(false);
  const isInitialMount = useRef(true);
  const {date, data, lastModified, selectedLocation, locationTranslate, value: {value: currency}} = state;
  const classes = useStyles();

  const historyDataHandler = e => {
    setFlag(false);
    if (e.state) {
      const {historyCity, historyDate} = e.state;

      dispatch({
        type: SELECT_LOCATION,
        payload: historyCity
      });
      dispatch({
        type: SELECT_DATE,
        payload: historyDate
      });
      dispatch({
        type: LOCATION_TRANSLATE,
        payload: getCityTranslateByKey(historyCity.key)
      });
    }
  };

  useEffect(() => {
    dispatch({
      type: FETCH_BANKS_PENDING,
      payload: data
    });
    apiEntryPoint.finance.banks(currency, {date: dateFormatter(date), city: selectedLocation.key}).then(banksData => {
      if (banksData.length) {
        dispatch({
          type: FETCH_BANKS_SUCCESS,
          payload: banksData
        });
      } else {
        dispatch({
          type: CHANGE_CURRENCY,
          payload: DEFAULT_CURRENCY
        });
      }
    }).catch(e => {
      dispatch({
        type: FETCH_BANKS_ERROR,
        payload: []
      });
      if (e.type === 'timeout') {
        setTimeoutError(true);
      }
      throw new Error(e.message);
    });
  }, [currency, date, selectedLocation]);

  useEffect(() => {
    const historyState = {historyCity: selectedLocation, historyDate: date};
    const historyTitle = banksTitleText(locationTranslate[DEFAULT_LANGUAGE]?.locative, dateFormatterText(date));
    const isNotToday = dateFormatter(date) !== dateFormatter(new Date()) ? `?date=${dateFormatter(date)}` : '';
    const historyUrl = `${window.location.origin}/nurfin/exchange-rates-bank-kz/${selectedLocation.key}/${isNotToday}`;

    if (isInitialMount.current) {
      window.addEventListener('popstate', historyDataHandler);
      window.history.pushState(historyState, historyTitle, historyUrl);
      isInitialMount.current = false;
    } else {
      if (flag) {
        window.history.pushState(historyState, historyTitle, historyUrl);
      }
      // trigger custom event for change data in *Best-rates* widget
      window.dispatchEvent(new CustomEvent('change-request-currency-params', {
        detail: {date: dateFormatter(date), city: selectedLocation.key}
      }));

      document.title = historyTitle;
    }
    setFlag(true);
  }, [date, selectedLocation]);

  const h1Text = banksH1Text(locationTranslate[DEFAULT_LANGUAGE]?.locative, dateFormatterText(date));

  return (
    <div className={classes.widget}>
      <HeaderMetaTags>
        <meta
          name='article:published_time'
          property='article:published_time'
          content={getKzDateTime(lastModified, 'full')}
        />
        <meta
          property='article:modified_time'
          content={getKzDateTime(lastModified, 'full')}
        />
        <script type="application/ld+json">
          {getBanksJSON_LD(h1Text, data, currency)}
        </script>

      </HeaderMetaTags>
      <div className={classes.header}>
        <WidgetH1 text={h1Text} />
        <div className={classes.selectWrapper}>
          <DynamicComponent
            props={{changeLocation: dispatch, state}}
            onLoad={loadDesktopLocationSelector}
            isLoading={platform.desktop}
          />
          <div className={classes.dateWrapper}>
            <CustomDatePicker
              changeDate={dispatch}
              selected={date}
            />
          </div>
          <DynamicComponent
            props={{changeLocation: dispatch, state}}
            onLoad={loadMobileLocationSelector}
            isLoading={platform.mobile}
          />
        </div>
      </div>
      <div className={classes.currencyWrapper}>
        <DynamicComponent
          props={{allCurrencies: groupedCurrency, changeCurrency: dispatch, state}}
          onLoad={loadDesktopCurrencySelector}
          isLoading={platform.desktop}
        />
        <DynamicComponent
          props={{allCurrencies: groupedCurrency, changeCurrency: dispatch, state}}
          onLoad={loadMobileCurrencySelector}
          isLoading={platform.mobile}
        />
      </div>
      <Loader visibility={state.isFetching} />
      {data.length > 0
        ? <BanksTable dispatch={dispatch} state={state} />
        : null
      }
      {timeoutError && (
        <p style={{textAlign: 'center'}}>
          Превышено время ожидания, пожалуйста, попробуйте обновить страницу
        </p>
      )}
    </div>
  );
};
